import { Swiper } from 'swiper';
import { Navigation } from 'swiper/modules';
import {
    sortBrandItems
} from '../../utils/brand-popularity';

const PAGE_SIZE = 20;
const SLIDER_SECTION_ID = 'brand-list-products';
const EXCLUDED_PRODUCT_HANDLES = new Set(['message-card', 'wrapping', 'shopping-bag']);

export default class{

    /**
     * constructor
     */
    constructor(){
        this.section = document.querySelector('.s-brand-list');
        this.list = this.section?.querySelector('[data-brand-list-items]');
        this.sortSelect = this.section?.querySelector('[data-brand-list-sort]');
        this.loadMoreButton = this.section?.querySelector('[data-brand-list-load-more]');
        this.pagination = this.section?.querySelector('[data-brand-list-pagination]');
        this.sliders = new Map();
        this.sliderLoads = new Map();
        this.sliderInits = new Map();
        this.visibleCount = PAGE_SIZE;
    }

    /**
     * init
     */
    init(){
        if (!this.section || !this.list) return;

        if (this.sortSelect) {
            this.sortItems(this.sortSelect.value);
            this.sortSelect.addEventListener('change', () => {
                this.visibleCount = PAGE_SIZE;
                this.sortItems(this.sortSelect.value);
            });
        } else {
            this.applyVisibility();
        }

        if (this.loadMoreButton) {
            this.loadMoreButton.addEventListener('click', () => {
                this.visibleCount += PAGE_SIZE;
                this.applyVisibility();
            });
        }

        document.addEventListener('recently-viewed-products-init', () => {
            this.recently();
        });
    }

    /**
     * getItems
     */
    getItems(){
        return Array.from(this.list.querySelectorAll('.s-brand-list__item'));
    }

    /**
     * getCollectionUrl
     */
    getCollectionUrl(handle){
        const base = window.FoxThemeSettings?.base_url || window.location.origin;
        return `${base}/collections/${encodeURIComponent(handle)}?sections=${SLIDER_SECTION_ID}`;
    }

    /**
     * getProductsJsonUrl
     */
    getProductsJsonUrl(handle){
        const base = window.FoxThemeSettings?.base_url || window.location.origin;
        return `${base}/collections/${encodeURIComponent(handle)}/products.json?limit=10`;
    }

    /**
     * extractSectionHtml
     */
    extractSectionHtml(responseText){
        try {
            const data = JSON.parse(responseText);
            return data[SLIDER_SECTION_ID] || '';
        } catch (error) {
            const doc = new DOMParser().parseFromString(responseText, 'text/html');
            const section = doc.querySelector(`#shopify-section-${SLIDER_SECTION_ID}`);
            const wrapper = section?.querySelector('[data-brand-list-products]');

            return wrapper ? wrapper.outerHTML : '';
        }
    }

    /**
     * hydrateSliderContent
     */
    hydrateSliderContent(container){
        container.querySelectorAll('responsive-image').forEach((element) => {
            const img = element.querySelector('img');
            if (!img) return;

            element.removeAttribute('data-image-loading');
            img.classList.add('f-img-loaded');

            if (!img.complete) {
                img.addEventListener('load', () => {
                    element.removeAttribute('data-image-loading');
                    img.classList.add('f-img-loaded');
                }, { once: true });
            }

            if (typeof element.loadImage === 'function') {
                element.loadImage();
            }
        });
    }

    /**
     * insertSliderSlides
     */
    insertSliderSlides(swiperWrapper, markup){
        if (!markup) return false;

        const template = document.createElement('template');
        template.innerHTML = markup.trim();

        const source = template.content.querySelector('[data-brand-list-products]')
            || template.content.querySelector('.swiper-wrapper');

        if (!source) return false;

        const slides = Array.from(source.querySelectorAll('.swiper-slide'));
        if (!slides.length) return false;

        swiperWrapper.replaceChildren(...slides);
        this.hydrateSliderContent(swiperWrapper);
        return true;
    }

    /**
     * buildSlideFromProduct
     */
    buildSlideFromProduct(product){
        const image = product.images?.[0];
        const imageHtml = image
            ? `<img src="${image.src}" alt="${product.title.replace(/"/g, '&quot;')}" loading="lazy" class="f-img-loaded" style="width:100%;height:auto;opacity:1;">`
            : '';
        const price = product.variants?.[0]?.price;
        const priceHtml = typeof window.formatMoney === 'function' && price != null
            ? window.formatMoney(price, window.FoxThemeSettings?.money_format)
            : '';

        return `
            <div class="swiper-slide">
                <div class="c-product-card product-card grid-list__column">
                    <div class="product-card__wrapper f-image-hover-effect__zoom">
                        <a href="${product.url}" class="full-width-link product-card__link">
                            <span class="visually-hidden">${product.title}</span>
                        </a>
                        <div class="product-card__image-wrapper f-card product-card__image-wrapper--main-only">
                            ${imageHtml}
                        </div>
                        <div class="product-card__info">
                            <h3 class="product-card__title font-body">
                                <a href="${product.url}">${product.title}</a>
                            </h3>
                            <div class="product-card__price">${priceHtml}</div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * loadSliderProductsFromJson
     */
    async loadSliderProductsFromJson(handle, swiperWrapper){
        const response = await fetch(this.getProductsJsonUrl(handle));
        if (!response.ok) {
            throw new Error(`Failed to load brand products json: ${response.status}`);
        }

        const data = await response.json();
        const slides = (data.products || [])
            .filter((product) => !EXCLUDED_PRODUCT_HANDLES.has(product.handle))
            .map((product) => this.buildSlideFromProduct(product))
            .join('');

        if (!slides) return false;

        const template = document.createElement('template');
        template.innerHTML = slides.trim();
        const slideNodes = Array.from(template.content.querySelectorAll('.swiper-slide'));

        if (!slideNodes.length) return false;

        swiperWrapper.replaceChildren(...slideNodes);
        return true;
    }

    /**
     * loadSliderProducts
     */
    async loadSliderProducts(item){
        const handle = item.dataset.brandHandle;
        const target = item.querySelector('[data-brand-list-slider-target]');
        const swiperWrapper = target?.querySelector('.swiper-wrapper');
        if (!handle || !swiperWrapper) return false;

        if (this.sliderLoads.has(item)) {
            return this.sliderLoads.get(item);
        }

        const loadPromise = fetch(this.getCollectionUrl(handle))
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to load brand products: ${response.status}`);
                }

                return response.text();
            })
            .then(async (responseText) => {
                const sectionHtml = this.extractSectionHtml(responseText);

                if (this.insertSliderSlides(swiperWrapper, sectionHtml)) {
                    return true;
                }

                return this.loadSliderProductsFromJson(handle, swiperWrapper);
            })
            .catch(async (error) => {
                console.error(error);

                try {
                    return await this.loadSliderProductsFromJson(handle, swiperWrapper);
                } catch (fallbackError) {
                    console.error(fallbackError);
                    return false;
                }
            });

        this.sliderLoads.set(item, loadPromise);
        return loadPromise;
    }

    /**
     * initSliderForItem
     */
    async initSliderForItem(item){
        if (this.sliders.has(item)) return;

        if (this.sliderInits.has(item)) {
            return this.sliderInits.get(item);
        }

        const initPromise = (async () => {
            const slider = item.querySelector('[data-brand-list-slider]');
            if (!slider) return;

            const target = slider.querySelector('[data-brand-list-slider-target]');
            if (!target) return;

            const swiperWrapper = target.querySelector('.swiper-wrapper');
            if (!swiperWrapper) return;

            if (!swiperWrapper.querySelector('.swiper-slide')) {
                await this.loadSliderProducts(item);
            } else {
                this.hydrateSliderContent(swiperWrapper);
            }

            if (!swiperWrapper.querySelector('.swiper-slide')) return;

            const swiper = new Swiper(target, {
                modules: [Navigation],
                observer: true,
                observeParents: true,
                slidesPerView: 2.25,
                spaceBetween: 16,
                breakpoints: {
                    768: {
                        slidesPerView: 3,
                        spaceBetween: 27
                    },
                    1024: {
                        slidesPerView: 4
                    },
                    1280: {
                        slidesPerView: 5
                    }
                },
                navigation: {
                    nextEl: slider.querySelector('[data-brand-list-slider-next]'),
                    prevEl: slider.querySelector('[data-brand-list-slider-prev]'),
                }
            });

            this.sliders.set(item, swiper);
        })();

        this.sliderInits.set(item, initPromise);
        return initPromise;
    }

    /**
     * applyVisibility
     */
    applyVisibility(){
        const items = this.getItems();

        items.forEach((item, index) => {
            const isVisible = index < this.visibleCount;
            item.classList.toggle('s-brand-list__item--hidden', !isVisible);

            if (isVisible) {
                this.initSliderForItem(item).then(() => {
                    const swiper = this.sliders.get(item);
                    if (!swiper) return;

                    swiper.update();
                    swiper.navigation?.update?.();
                });
            }
        });

        if (this.pagination) {
            const hasMore = this.visibleCount < items.length;
            this.pagination.hidden = !hasMore;
        }
    }

    /**
     * sortItems
     */
    sortItems(sortBy){
        sortBrandItems(this.list, '.s-brand-list__item', sortBy);
        this.applyVisibility();
    }

    /**
     * recently
     */
    recently(){
        const productContainer = document.querySelector('[data-recently-collection-slider]');
        if (!productContainer) return;

        const target = productContainer.querySelector('[data-recently-collection-slider-target]');
        new Swiper(target, {
            modules: [Navigation],
            slidesPerView: 2.25,
            spaceBetween: 16,
            breakpoints: {
                768: {
                    slidesPerView: 3,
                    spaceBetween: 27
                },
                1024: {
                    slidesPerView: 4
                },
                1280: {
                    slidesPerView: 5
                }
            },
            navigation: {
                nextEl: productContainer.querySelector('[data-recently-collection-slider-next]'),
                prevEl: productContainer.querySelector('[data-recently-collection-slider-prev]'),
            }
        });
    }
}
